// Voicemail drops (ADR 0022): the company switch, the notice and its acknowledgment, the drop
// records and the dismissed message warnings, and a campaign's default message. Mirrors
// crates/api-types/src/vmdrop.rs. State rules are the company's one table, edited through the
// Parallel dialing endpoints (api/pdial.ts).
import { queryOptions } from "@tanstack/react-query";
import { API_BASE } from "../config";
import { api, buildQuery } from "./client";

export type VmMessageScope = "personal" | "team";
export type VmMessageStatus = "checking" | "ready" | "archived";
export type ReviewKind = "ai" | "basic";
export type DropState =
  | "requested"
  | "playing"
  | "left"
  | "partial"
  | "failed"
  | "customer_hung_up"
  | "unknown"
  | "refused";
export type BeepSource = "tone" | "silence" | "timeout";
export type LineType = "landline" | "wireless" | "voip" | "unknown";

export interface NoticeItem {
  id: string;
  text: string;
}

export interface Notice {
  title: string;
  intro: string;
  paragraphs: string[];
  sources: { label: string; url: string }[];
  items: NoticeItem[];
  agreement: string;
}

export interface VmAckView {
  id: string;
  user_id: string;
  user_name?: string | null;
  acknowledged_at: string;
  notice_version: string;
  items: string[];
}

export interface DropStateRule {
  state: string;
  state_name: string;
  no_message: boolean;
}

export interface RepDropCounts {
  user_id: string;
  user_name: string;
  left: number;
  partial: number;
  not_left: number;
  refused: number;
}

export interface VmDropsAdmin {
  enabled: boolean;
  enabled_at?: string | null;
  enabled_by_name?: string | null;
  /** The notice with the company's name filled in. */
  notice: Notice;
  notice_version: string;
  /** The latest acknowledgment, of any version. */
  acknowledgment?: VmAckView | null;
  /** The latest acknowledgment is of the current notice. */
  ack_current: boolean;
  voip_as_cell: boolean;
  state_rules: DropStateRule[];
  counts: RepDropCounts[];
}

export interface DropRecord {
  id: string;
  call_id?: string | null;
  requested_at: string;
  user_id: string;
  user_name: string;
  /** Last four digits only (`***0199`). */
  number: string;
  line_type: LineType;
  state_code?: string | null;
  message_name?: string | null;
  state: DropState;
  refused_code?: string | null;
  refused_reason?: string | null;
  beep_source?: BeepSource | null;
  played_ms?: number | null;
  rule?: string | null;
}

export interface DropRecordPage {
  items: DropRecord[];
  next_cursor?: string | null;
}

export interface ReviewWarning {
  code: string;
  reason: string;
  quote?: string | null;
}

/** Someone chose to use a message with warnings: the record of responsibility. */
export interface DismissalRecord {
  id: string;
  dismissed_at: string;
  dismissed_by: string;
  dismissed_by_name: string;
  message_id: string;
  message_name: string;
  /** SHA-256 of the audio the warnings were about. */
  audio_sha256: string;
  review_id: string;
  review_kind: ReviewKind;
  review_version: string;
  review_model?: string | null;
  /** The warnings shown, as they were shown. */
  warnings: ReviewWarning[];
}

export interface DismissalPage {
  items: DismissalRecord[];
  next_cursor?: string | null;
}

export interface VmMessageBrief {
  id: string;
  name: string;
  duration_ms: number;
  scope: VmMessageScope;
  status: VmMessageStatus;
}

export interface CampaignVmDefault {
  message?: VmMessageBrief | null;
  /** The parent's default this campaign uses when it has none of its own. */
  inherited?: { campaign_id: string; campaign_name: string; message: VmMessageBrief } | null;
  /** The team's ready messages. */
  options: VmMessageBrief[];
  can_edit: boolean;
  drops_enabled: boolean;
}

export const vmKeys = {
  admin: ["admin", "vm-drops"] as const,
  records: (cursor: string | null) => ["admin", "vm-drops", "records", cursor ?? ""] as const,
  dismissals: (cursor: string | null) => ["admin", "vm-drops", "dismissals", cursor ?? ""] as const,
  campaign: (id: string) => ["admin", "vm-drops", "campaign", id] as const,
};

export const vmDropsQuery = queryOptions({
  queryKey: vmKeys.admin,
  queryFn: () => api<VmDropsAdmin>("/v1/admin/vm-drops"),
});

export const vmRecordsQuery = (cursor: string | null) =>
  queryOptions({
    queryKey: vmKeys.records(cursor),
    queryFn: () => api<DropRecordPage>("/v1/admin/vm-drops/records", { query: { cursor, limit: 50 } }),
  });

export const vmDismissalsQuery = (cursor: string | null) =>
  queryOptions({
    queryKey: vmKeys.dismissals(cursor),
    queryFn: () => api<DismissalPage>("/v1/admin/vm-drops/dismissals", { query: { cursor, limit: 50 } }),
  });

export const campaignVmDefaultQuery = (id: string) =>
  queryOptions({
    queryKey: vmKeys.campaign(id),
    queryFn: () => api<CampaignVmDefault>(`/v1/campaigns/${id}/vm-default`),
  });

export const vmDrops = {
  setEnabled: (enabled: boolean) =>
    api<VmDropsAdmin>("/v1/admin/vm-drops/settings", { method: "PUT", body: { enabled } }),
  acknowledge: (notice_version: string, items: string[]) =>
    api<VmDropsAdmin>("/v1/admin/vm-drops/acknowledge", {
      method: "POST",
      body: { notice_version, items, agree: true },
    }),
  setCampaignDefault: (campaignId: string, messageId: string | null) =>
    api<CampaignVmDefault>(`/v1/campaigns/${campaignId}/vm-default`, {
      method: "PUT",
      body: { message_id: messageId },
    }),
};

/** The CSV export's address (opened with the console's cookie). */
export function recordsCsvHref(): string {
  return `${API_BASE}/v1/admin/vm-drops/records${buildQuery({ format: "csv" })}`;
}

/** `m:ss`. */
export function clockMs(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export const RESULT: Record<DropState, { label: string; tone: "ok" | "warn" | "neutral" }> = {
  requested: { label: "Waiting for the beep", tone: "neutral" },
  playing: { label: "Playing", tone: "neutral" },
  left: { label: "Left", tone: "ok" },
  partial: { label: "Partly left", tone: "warn" },
  failed: { label: "Not left: didn't play", tone: "warn" },
  customer_hung_up: { label: "Not left: hung up first", tone: "warn" },
  unknown: { label: "Unknown", tone: "warn" },
  refused: { label: "Refused", tone: "neutral" },
};

export const LINE: Record<LineType, string> = {
  landline: "Landline",
  wireless: "Cell phone",
  voip: "VoIP",
  unknown: "Unknown",
};

export const BEEP: Record<BeepSource, string> = {
  tone: "Beep",
  silence: "Silence",
  timeout: "35 s limit",
};
